import { Building2, CalendarDays, Crown, BookOpen } from 'lucide-react';

/**
 * Popis druhů katalogových záznamů pro rozhraní.
 *
 * Používá to administrace i formulář, kterým členové posílají návrhy. Kdyby to
 * měl každý svoje, dřív nebo později by se rozešly a člověk by vyplňoval jiná
 * políčka, než jaká pak administrace vidí.
 *
 * Musí odpovídat tabulce DRUHY v backend/routes/directory.py.
 *
 * U každého popisku je vedle českého textu i `klic` do slovníku. Formulář
 * návrhu je součást Bloom World a musí umět anglicky; administrace je česky
 * a bere si `label`. Český text tu zůstává i jako záloha, kdyby překlad chyběl.
 */

export const DRUHY_KATALOGU = [
  {
    key: 'organizations',
    icon: Building2,
    label: 'Organizace',
    klic: 'sekce.organizations',
    jednotne: 'organizaci',
    klicJednotne: 'navrh.jednotne.organizations',
    popisProNavrh: 'Spolek, nezisková organizace nebo podpůrná skupina.',
    klicPopis: 'navrh.popis.organizations',
    pole: [
      { name: 'areas', label: 'Oblasti činnosti', klic: 'detail.oblasti', typ: 'seznam' },
      { name: 'services', label: 'Co nabízí', klic: 'detail.coNabizi', typ: 'seznam' },
    ],
  },
  {
    key: 'events',
    icon: CalendarDays,
    label: 'Akce',
    klic: 'sekce.events',
    jednotne: 'akci',
    klicJednotne: 'navrh.jednotne.events',
    popisProNavrh: 'Setkání, Pride, workshop, konference.',
    klicPopis: 'navrh.popis.events',
    pole: [
      { name: 'starts_at', label: 'Začátek', klic: 'detail.zacatek', typ: 'datum', povinne: true },
      { name: 'ends_at', label: 'Konec', klic: 'detail.konec', typ: 'datum' },
      { name: 'format', label: 'Forma', klic: 'navrh.forma', typ: 'vyber', vychozi: 'offline',
        moznosti: [['offline', 'Osobně'], ['online', 'Online'], ['hybrid', 'Obojí']],
        klicMoznosti: 'detail.forma' },
      { name: 'url', label: 'Odkaz na akci', klic: 'navrh.odkazAkce' },
      { name: 'organizer_name', label: 'Pořadatel', klic: 'detail.poradatel' },
    ],
  },
  {
    key: 'pageants',
    icon: Crown,
    label: 'Soutěže',
    klic: 'sekce.pageants',
    jednotne: 'soutěž',
    klicJednotne: 'navrh.jednotne.pageants',
    popisProNavrh: 'Trans soutěž krásy – termín, přihlášky, pořadatel.',
    klicPopis: 'navrh.popis.pageants',
    pole: [
      { name: 'organizer_name', label: 'Pořadatel', klic: 'detail.poradatel' },
      { name: 'starts_at', label: 'Termín', klic: 'detail.termin', typ: 'datum' },
      { name: 'registration_deadline', label: 'Uzávěrka přihlášek',
        klic: 'detail.uzaverka', typ: 'datum' },
      { name: 'registration_url', label: 'Odkaz na přihlášku', klic: 'navrh.odkazPrihlaska' },
      { name: 'eligibility', label: 'Kdo se může přihlásit', klic: 'detail.kdoSeMuze' },
      { name: 'scope', label: 'Rozsah', klic: 'navrh.rozsah', typ: 'vyber', vychozi: 'national',
        moznosti: [['local', 'Místní'], ['national', 'Celostátní'], ['international', 'Mezinárodní']],
        klicMoznosti: 'detail.rozsah' },
      { name: 'edition_status', label: 'Stav ročníku', klic: 'detail.stavRocniku',
        typ: 'vyber', vychozi: 'upcoming',
        moznosti: [['upcoming', 'Chystá se'], ['registration_open', 'Přihlášky otevřené'],
                   ['registration_closed', 'Přihlášky uzavřené'], ['completed', 'Proběhlo']],
        klicMoznosti: 'detail.rocnik' },
    ],
  },
  {
    key: 'resources',
    icon: BookOpen,
    label: 'Rozcestník',
    klic: 'sekce.resources',
    jednotne: 'rozcestník',
    klicJednotne: 'navrh.jednotne.resources',
    popisProNavrh: 'Praktická informace – zdravotní péče, právo, doklady, kontakty.',
    klicPopis: 'navrh.popis.resources',
    pole: [
      { name: 'scope', label: 'Platí pro', klic: 'navrh.platiPro', typ: 'vyber', vychozi: 'country',
        moznosti: [['global', 'Celý svět'], ['country', 'Zemi'], ['region', 'Oblast'], ['city', 'Město']],
        klicMoznosti: 'navrh.platnost' },
      { name: 'topic', label: 'Téma', klic: 'detail.tema' },
      { name: 'body', label: 'Text', klic: 'navrh.text', typ: 'dlouhy' },
      { name: 'links', label: 'Odkazy', klic: 'navrh.odkazy', typ: 'seznam' },
    ],
  },
];

export const najdiDruh = (key) => DRUHY_KATALOGU.find((d) => d.key === key);

/** Prázdný formulář se předvyplněnými výchozími hodnotami daného druhu. */
export const prazdnyFormular = (druh, zeme = '') => {
  const details = {};
  (druh?.pole || []).forEach((p) => { if (p.vychozi) details[p.name] = p.vychozi; });
  return {
    name: '', description: '', country_iso2: zeme, region_id: '', city_name: '', address: '',
    email: '', phone: '', website: '', categories: '', source: '', details,
  };
};

/**
 * Poskládá tělo požadavku. Políčka navíc jdou naplocho vedle ostatních –
 * server si je sám roztřídí do `details`.
 */
export const seskladejTelo = (druh, formular) => {
  const detaily = {};
  (druh?.pole || []).forEach((p) => {
    const v = formular.details[p.name];
    if (v === undefined || v === '') return;
    if (p.typ === 'seznam') {
      detaily[p.name] = String(v).split(',').map((x) => x.trim()).filter(Boolean);
    } else if (p.typ === 'datum') {
      detaily[p.name] = new Date(v).toISOString();
    } else {
      detaily[p.name] = v;
    }
  });

  return {
    name: formular.name,
    description: formular.description,
    location: {
      country_iso2: formular.country_iso2,
      region_id: formular.region_id || null,
      city_name: formular.city_name,
      address: formular.address,
    },
    email: formular.email,
    phone: formular.phone,
    social: { website: formular.website },
    categories: formular.categories.split(',').map((c) => c.trim()).filter(Boolean),
    source: formular.source,
    ...detaily,
  };
};

/** Co musí být vyplněné, aby mělo smysl formulář odeslat. */
export const zkontroluj = (t, druh, formular) => {
  if (!formular.name.trim()) return t('navrh.doplnteNazev');
  if (!formular.country_iso2) return t('navrh.vyberteZemi');
  const chybi = (druh?.pole || []).find((p) => p.povinne && !formular.details[p.name]);
  if (chybi) return t('navrh.doplnte', { co: t(chybi.klic || '').toLowerCase() });
  return null;
};
